import { PrimaryButton, SecondaryButton } from "@brexhq/component-library";
import styled from "@emotion/styled";
import { padding } from "polished";
import * as React from "react";

import { Container } from "@brexhq/next/src/components/layout/Container/Container";
import { titleMargin } from "@brexhq/next/src/components/modules/FormMultistep/FormMultistep.elements";
import { useLeadFormModuleContext } from "@brexhq/next/src/components/modules/LeadForm/LeadFormModuleProvider";
import { LeadFormSteps } from "@brexhq/next/src/components/modules/LeadForm/machines/types";
import { ErrorBoundary } from "@brexhq/next/src/components/shared/ErrorBoundary";
import { DarkModeOverride } from "@brexhq/next/src/contexts/Theme";
import useAnalytics from "@brexhq/next/src/hooks/useAnalytics";
import {
  LeadFormViewedEvent,
  LeadFormSubmittedEvent,
} from "@brexhq/next/src/telemetry/analyticsEvents";
import { TitleXS } from "@brexhq/shared/components/Typography/Components";
import { isDarkTheme } from "@brexhq/shared/types/ModuleTheme";

import { SingleFlexContainer } from "../GTMSteps/elements";
import { RedirectLoading } from "../Loading/Loading";

import { SubTitle } from "./PageTemplate.elements";
import { ActionMeta } from "./SalesPageTemplate";

const Wrapper = styled.div<{ inlineForm?: boolean; marginTop?: number }>(
  ({ theme, inlineForm }) =>
    inlineForm
      ? {}
      : {
          borderRadius: theme.borders.radius.default,
          ...padding(theme.spacing[40]),
          background: theme.colors.background.secondary,
        },
);

const DynamicContainer = styled(Container)<{
  inlineFormWidth?: string;
  inlineForm?: boolean;
}>(({ theme, inlineFormWidth, inlineForm }) => ({
  width: "100%", // Default to full width
  [`${theme.breakpoints.LRG}`]: {
    width: inlineForm && inlineFormWidth ? inlineFormWidth : "100%", // Apply custom width only for non-mobile screens
  },
}));

export interface GTMFormPageTemplateProps {
  title?: string;
  step: LeadFormSteps;
  subtitle?: string | React.ReactNode;
  showSignIn?: boolean;
  programName: string;
  nextAction?: ActionMeta;
  prevAction?: Pick<ActionMeta, "label" | "onClick">;
  confirmationOnly?: boolean;
  noContent?: boolean;
  inlineForm?: boolean;
  inlineEmailFieldWidth?: string;
}

export const GTMFormPageTemplate: React.FC<
  React.PropsWithChildren<GTMFormPageTemplateProps>
> = ({
  title,
  subtitle,
  children,
  step,
  programName,
  nextAction,
  prevAction,
  confirmationOnly,
  noContent,
  inlineForm,
  inlineEmailFieldWidth,
}) => {
  const { trackEvent } = useAnalytics();
  const { theme, marginTop = "none" } = useLeadFormModuleContext();

  React.useEffect(() => {
    if (!inlineForm) {
      window.scrollTo(0, 0);
    }

    trackEvent(new LeadFormViewedEvent(step, programName));
    // add page analytics here

    // Disabling below because we want this to run once and only once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const next = React.useCallback(
    (e) => {
      trackEvent(new LeadFormSubmittedEvent(step, programName));
      nextAction?.onClick(e);
    },
    [nextAction, programName, step, trackEvent],
  );

  const prev = React.useCallback(() => {
    prevAction?.onClick();
  }, [prevAction]);

  if (noContent) {
    return <RedirectLoading />;
  }

  return (
    <DynamicContainer
      width="100%"
      margin={[marginTop, 0, 0]}
      inlineForm={inlineForm}
      inlineFormWidth={inlineEmailFieldWidth}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          next(e);
        }}
        style={{
          width: "100%",
        }}
      >
        <DarkModeOverride useDarkMode={isDarkTheme(theme)}>
          <ErrorBoundary>
            <Wrapper inlineForm={inlineForm}>
              <>
                {title && (
                  <TitleXS margin={titleMargin}>
                    <div className="_lr-hide dd-privacy-mask">{title}</div>
                  </TitleXS>
                )}
                {subtitle && <SubTitle>{subtitle}</SubTitle>}
              </>
              {children}
              {(prevAction || nextAction) && (
                <Container
                  justifyContent={confirmationOnly ? "flex-start" : "flex-end"}
                  gap="sml"
                  width="100%"
                >
                  {prevAction && (
                    <SingleFlexContainer>
                      <SecondaryButton isFullWidth onClick={prev}>
                        Back
                      </SecondaryButton>
                    </SingleFlexContainer>
                  )}
                  {nextAction && (
                    <SingleFlexContainer>
                      <PrimaryButton isFullWidth onClick={next}>
                        Continue
                      </PrimaryButton>
                    </SingleFlexContainer>
                  )}
                </Container>
              )}
            </Wrapper>
          </ErrorBoundary>
        </DarkModeOverride>
      </form>
    </DynamicContainer>
  );
};
